import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Store, Star, Navigation, ExternalLink } from 'lucide-react';
import farmRecommendationAgent from '../../services/agents/farmRecommendationAgent';
import linkingHelper from '../../utils/linkingHelper';

interface Farm {
  id: string;
  name: string;
  location: {
    lat: number;
    lng: number;
    address: string;
  };
  categories: string[];
  products: string[];
  rating?: number;
  distance?: number;
  certifications?: string[];
  specialty?: string[];
}

interface WhereToBuyWidgetProps {
  productName: string;
  maxFarms?: number;
  title?: string;
  showRating?: boolean;
  showDistance?: boolean;
}

const WhereToBuyWidget: React.FC<WhereToBuyWidgetProps> = ({
  productName,
  maxFarms = 6,
  title,
  showRating = true,
  showDistance = true,
}) => {
  const [farms, setFarms] = useState<Farm[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const displayTitle = title || `Where to Buy ${productName}`;

  useEffect(() => {
    const fetchFarms = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!productName) {
          setFarms([]);
          setLoading(false);
          return;
        }

        // Get farms selling this specific product
        const foundFarms = farmRecommendationAgent.getFarmsByProduct(
          productName,
          maxFarms
        );

        setFarms(foundFarms);
      } catch (err) {
        console.error('Error fetching farms:', err);
        setError('Unable to load farm locations');
      } finally {
        setLoading(false);
      }
    };

    fetchFarms();
  }, [productName, maxFarms]);

  const styles = {
    widget: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '24px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '12px',
      borderBottom: '2px solid #2D5A27',
    },
    headerIcon: {
      color: '#2D5A27',
      marginRight: '12px',
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: 0,
    },
    description: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '20px',
    },
    farmList: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px',
    },
    farmItem: {
      backgroundColor: '#f9f9f9',
      borderRadius: '6px',
      padding: '14px',
      border: '1px solid #e5e5e5',
      transition: 'all 0.3s ease',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    } as React.CSSProperties,
    farmItemHover: {
      backgroundColor: '#f0f7ef',
      borderColor: '#2D5A27',
      transform: 'translateX(4px)',
    },
    farmInfo: {
      flex: 1,
    },
    farmName: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#1a1a1a',
      marginBottom: '6px',
      display: 'flex',
      alignItems: 'center',
    },
    farmMeta: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '4px',
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '13px',
      color: '#666',
    },
    metaIcon: {
      width: '14px',
      height: '14px',
      marginRight: '6px',
      color: '#2D5A27',
    },
    rating: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '13px',
      color: '#f59e0b',
      fontWeight: '600',
    },
    arrow: {
      color: '#2D5A27',
      opacity: 0.5,
      transition: 'opacity 0.3s ease',
    },
    loading: {
      textAlign: 'center' as const,
      padding: '32px 0',
      color: '#666',
    },
    empty: {
      textAlign: 'center' as const,
      padding: '24px',
      color: '#666',
      backgroundColor: '#f9f9f9',
      borderRadius: '6px',
    },
    emptyIcon: {
      width: '48px',
      height: '48px',
      margin: '0 auto 12px',
      color: '#ccc',
    },
    viewOnMap: {
      display: 'inline-flex',
      alignItems: 'center',
      marginTop: '16px',
      padding: '10px 16px',
      backgroundColor: '#2D5A27',
      color: '#fff',
      borderRadius: '6px',
      fontWeight: '600',
      textDecoration: 'none',
      fontSize: '14px',
      transition: 'background-color 0.3s ease',
    },
    viewOnMapIcon: {
      width: '16px',
      height: '16px',
      marginRight: '8px',
    },
    viewAll: {
      display: 'inline-flex',
      alignItems: 'center',
      marginTop: '16px',
      color: '#2D5A27',
      fontWeight: '600',
      textDecoration: 'none',
      fontSize: '14px',
      transition: 'color 0.3s ease',
    },
    viewAllIcon: {
      width: '16px',
      height: '16px',
      marginLeft: '6px',
    },
  };

  if (loading) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}>
          <MapPin style={styles.headerIcon} size={24} />
          <h3 style={styles.title}>{displayTitle}</h3>
        </div>
        <div style={styles.loading}>
          <div className="spinner-border text-success" role="status">
            <span className="sr-only">Loading farms...</span>
          </div>
          <p style={{ marginTop: '12px' }}>Finding local sources...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}>
          <MapPin style={styles.headerIcon} size={24} />
          <h3 style={styles.title}>{displayTitle}</h3>
        </div>
        <div style={styles.empty}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (farms.length === 0) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}>
          <MapPin style={styles.headerIcon} size={24} />
          <h3 style={styles.title}>{displayTitle}</h3>
        </div>
        <div style={styles.empty}>
          <Store style={styles.emptyIcon} />
          <p>
            No farms currently selling {productName}.
            <br />
            Check our directory for similar produce.
          </p>
          <Link to={linkingHelper.getSearchUrl(productName)} style={styles.viewAll}>
            Search Our Directory
            <ExternalLink style={styles.viewAllIcon} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.widget}>
      <div style={styles.header}>
        <MapPin style={styles.headerIcon} size={24} />
        <h3 style={styles.title}>{displayTitle}</h3>
      </div>

      <p style={styles.description}>
        {farms.length} {farms.length === 1 ? 'farm' : 'farms'} selling fresh {productName} near you
      </p>

      <div style={styles.farmList}>
        {farms.map((farm) => (
          <Link
            key={farm.id}
            to={linkingHelper.getFarmUrl(farm.id)}
            style={{ textDecoration: 'none' }}
          >
            <div
              style={styles.farmItem}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, styles.farmItemHover);
                const arrow = e.currentTarget.querySelector('.arrow-icon') as HTMLElement;
                if (arrow) arrow.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f9f9f9';
                e.currentTarget.style.borderColor = '#e5e5e5';
                e.currentTarget.style.transform = 'translateX(0)';
                const arrow = e.currentTarget.querySelector('.arrow-icon') as HTMLElement;
                if (arrow) arrow.style.opacity = '0.5';
              }}
            >
              <div style={styles.farmInfo}>
                <div style={styles.farmName}>
                  <Store size={16} style={{ marginRight: '8px', color: '#2D5A27' }} />
                  {farm.name}
                </div>

                <div style={styles.farmMeta}>
                  {farm.location && (
                    <div style={styles.metaItem}>
                      <MapPin style={styles.metaIcon} />
                      {farm.location.address}
                      {showDistance && farm.distance && (
                        <span style={{ marginLeft: '8px', fontWeight: '600', color: '#2D5A27' }}>
                          • {farm.distance.toFixed(1)} mi
                        </span>
                      )}
                    </div>
                  )}

                  {showRating && farm.rating && (
                    <div style={styles.rating}>
                      <Star style={{ ...styles.metaIcon, fill: '#f59e0b' }} />
                      {farm.rating.toFixed(1)} rating
                    </div>
                  )}
                </div>
              </div>

              <div className="arrow-icon" style={styles.arrow}>
                <ExternalLink size={18} />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {farms.length > 0 && (
        <>
          <Link
            to="/map"
            style={styles.viewOnMap}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#1a3d1a';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#2D5A27';
            }}
          >
            <Navigation style={styles.viewOnMapIcon} />
            View on Map
          </Link>

          {farms.length >= maxFarms && (
            <Link
              to={linkingHelper.getSearchUrl(productName)}
              style={{ ...styles.viewAll, marginLeft: '12px' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#1a3d1a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#2D5A27';
              }}
            >
              View All Results
              <ExternalLink style={styles.viewAllIcon} />
            </Link>
          )}
        </>
      )}
    </div>
  );
};

export default WhereToBuyWidget;
